/**
 * Axios instance pre-configured for the Burmazon API.
 *
 * Automatically attaches the access token from Zustand auth store and
 * handles 401 → token refresh → retry logic.
 */

import type { ApiResponse, AuthTokens, PaginationMeta } from '@burmazon/shared';
import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';

const BASE_URL = import.meta.env.VITE_API_URL ?? '/api/v1';

export const apiClient = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

// ── Token storage (in-memory) ────────────────────────────────────────────────
let accessToken: string | null = null;

type TokenSubscriber = () => void;
const tokenSubscribers = new Set<TokenSubscriber>();

export function setAccessToken(token: string | null): void {
  accessToken = token;
  tokenSubscribers.forEach((fn) => fn());
}

export function getAccessToken(): string | null {
  return accessToken;
}

export const subscribeToToken = (fn: TokenSubscriber): (() => void) => {
  tokenSubscribers.add(fn);
  return () => tokenSubscribers.delete(fn);
};

// ── Force-logout subscribers ─────────────────────────────────────────────────
// Called when token refresh fails. Listeners clear auth state; React Router
// (ProtectedRoute) then handles redirecting to /login on protected pages.
// Guest-friendly pages like /checkout stay on screen without a hard redirect.
type ForceLogoutSubscriber = () => void;
const forceLogoutSubscribers = new Set<ForceLogoutSubscriber>();

export function subscribeToForceLogout(fn: ForceLogoutSubscriber): () => void {
  forceLogoutSubscribers.add(fn);
  return () => forceLogoutSubscribers.delete(fn);
}

function triggerForceLogout(): void {
  forceLogoutSubscribers.forEach((fn) => fn());
}

// ── Request interceptor — attach access token ─────────────────────────────
apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

// ── Error extraction ──────────────────────────────────────────────────────
function extractError(error: AxiosError): Error {
  const serverMessage = (error.response?.data as { error?: { message?: string } })?.error?.message;
  if (serverMessage) return new Error(serverMessage);

  if (!error.response) {
    if (error.code === 'ECONNABORTED' || error.message.toLowerCase().includes('timeout')) {
      return new Error('Request timed out — please check your connection and try again');
    }
    return new Error('Unable to reach the server — please check your network connection');
  }

  const fallbacks: Record<number, string> = {
    400: 'Invalid request — please check your input',
    401: 'Session expired — please log in again',
    403: 'You do not have permission to perform this action',
    404: 'The requested resource was not found',
    409: 'This action conflicts with existing data',
    422: 'Validation failed — please check your input',
    429: 'Too many requests — please wait a moment and try again',
    500: 'A server error occurred — please try again later',
    502: 'Service temporarily unavailable — please try again',
    503: 'Service temporarily unavailable — please try again',
  };
  const status = error.response.status;
  return new Error(fallbacks[status] ?? `Request failed (${status})`);
}

// ── Response interceptor — handle 401 → refresh → retry ──────────────────
let refreshPromise: Promise<AuthTokens> | null = null;

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // Only one refresh call at a time (concurrent requests share the promise)
        if (!refreshPromise) {
          const storedRefreshToken = localStorage.getItem('refreshToken');
          if (!storedRefreshToken) throw new Error('No refresh token');

          refreshPromise = axios
            .post<{ success: true; data: { tokens: AuthTokens } }>(`${BASE_URL}/auth/refresh`, {
              refreshToken: storedRefreshToken,
            })
            .then((res) => res.data.data.tokens)
            .finally(() => {
              refreshPromise = null;
            });
        }

        const tokens = await refreshPromise;
        setAccessToken(tokens.accessToken);
        localStorage.setItem('refreshToken', tokens.refreshToken);

        originalRequest.headers.Authorization = `Bearer ${tokens.accessToken}`;
        return apiClient(originalRequest);
      } catch {
        // Refresh failed — clear tokens and notify subscribers to reset auth state.
        // ProtectedRoute will redirect to /login on protected pages; guest-friendly
        // pages (e.g. /checkout) stay on screen with the user treated as a guest.
        setAccessToken(null);
        localStorage.removeItem('refreshToken');
        triggerForceLogout();
      }
    }

    return Promise.reject(extractError(error));
  },
);

// ── Typed request helpers ─────────────────────────────────────────────────

export async function apiGet<T>(url: string, params?: Record<string, unknown>): Promise<T> {
  const res = await apiClient.get<ApiResponse<T>>(url, { params });
  if (!res.data.success) throw new Error(res.data.error.message);
  return res.data.data;
}

export async function apiPost<T>(url: string, data?: unknown): Promise<T> {
  const res = await apiClient.post<ApiResponse<T>>(url, data);
  if (!res.data.success) throw new Error(res.data.error.message);
  return res.data.data;
}

export async function apiPatch<T>(url: string, data?: unknown): Promise<T> {
  const res = await apiClient.patch<ApiResponse<T>>(url, data);
  if (!res.data.success) throw new Error(res.data.error.message);
  return res.data.data;
}

export async function apiPut<T>(url: string, data?: unknown): Promise<T> {
  const res = await apiClient.put<ApiResponse<T>>(url, data);
  if (!res.data.success) throw new Error(res.data.error.message);
  return res.data.data;
}

export async function apiDelete(url: string): Promise<void> {
  await apiClient.delete(url);
}

/** For paginated list endpoints that return { success, data: T[], meta }. */
export async function apiGetPaginated<T>(
  url: string,
  params?: Record<string, unknown>,
): Promise<{ data: T; meta: PaginationMeta }> {
  const res = await apiClient.get<ApiResponse<T>>(url, { params });
  if (!res.data.success) throw new Error(res.data.error.message);
  return { data: res.data.data, meta: res.data.meta! };
}
